import React from 'react'
import { Link } from 'react-router-dom'
import { PlusIcon, UserGroupIcon } from '@heroicons/react/24/outline'
import { Subscription } from '../../types'
import SubscriptionCard from './SubscriptionCard'
import Skeleton from '../Skeleton'
import { isCurrent } from './subscriptionsView'

interface SubscriptionsListProps {
  subscriptions: Subscription[]
  onCancel: (subscription: Subscription) => void
  onReactivate: (subscription: Subscription) => void
  onManage?: (subscription: Subscription) => void
  onUpdateCard?: (subscription: Subscription) => void
  isLoading?: boolean
  error?: string | null
  onRetry?: () => void
}

export const SubscriptionCardSkeleton: React.FC = () => (
  <div className="rounded-xl border border-gray-200 bg-white p-5" data-testid="subscription-skeleton">
    <div className="flex items-center gap-3">
      <Skeleton className="h-12 w-12 rounded-full" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-3 w-1/4" />
      </div>
      <Skeleton className="h-5 w-16 rounded-full" />
    </div>
    <Skeleton className="mt-4 h-16 w-full rounded-lg" />
    <Skeleton className="mt-3 h-4 w-1/2" />
    <div className="mt-4 flex gap-2">
      <Skeleton className="h-9 w-20 rounded-lg" />
      <Skeleton className="h-9 w-20 rounded-lg" />
    </div>
  </div>
)

/** A stack of people with a plus: "nobody here yet, go find someone". */
const EmptyIllustration: React.FC = () => (
  <div className="relative mx-auto mb-5 h-20 w-20" aria-hidden="true">
    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 ring-1 ring-white/10" />
    <UserGroupIcon className="absolute inset-0 m-auto h-10 w-10 text-accent" />
    <span className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full bg-accent shadow-md">
      <PlusIcon className="h-4 w-4 text-terminal-bg" strokeWidth={2.5} />
    </span>
  </div>
)

export const ErrorPanel: React.FC<{ message: string; onRetry?: () => void }> = ({ message, onRetry }) => (
  <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-5 py-6 text-center">
    <p className="text-sm font-medium text-red-700">{message}</p>
    {onRetry && (
      <button
        type="button"
        onClick={onRetry}
        className="mt-3 rounded-lg border border-gray-300 bg-white px-3.5 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
      >
        Try again
      </button>
    )}
  </div>
)

const SubscriptionsList: React.FC<SubscriptionsListProps> = ({
  subscriptions,
  onCancel,
  onReactivate,
  onManage,
  onUpdateCard,
  isLoading = false,
  error = null,
  onRetry,
}) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2" aria-busy="true" aria-label="Loading subscriptions">
        <SubscriptionCardSkeleton />
        <SubscriptionCardSkeleton />
      </div>
    )
  }

  if (error) return <ErrorPanel message={error} onRetry={onRetry} />

  // Expired ones, and cancelled ones whose paid time has run out, are history:
  // their payments stay under Transactions.
  const current = subscriptions.filter((sub) => isCurrent(sub))

  if (current.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-14 text-center motion-safe:animate-fade-in">
        <EmptyIllustration />
        <h3 className="text-lg font-semibold text-gray-900">No subscriptions yet</h3>
        <p className="mx-auto mt-1 max-w-sm text-sm text-gray-600">
          Subscribe to a creator to get their calls the moment they&rsquo;re locked.
        </p>
        <Link
          to="/leaderboard"
          className="mt-5 inline-flex items-center rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-terminal-bg transition-colors hover:bg-accent-hover"
        >
          Browse creators
        </Link>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 motion-safe:animate-fade-in">
      {current.map((subscription) => (
        <SubscriptionCard
          key={subscription.id}
          subscription={subscription}
          onCancel={onCancel}
          onReactivate={onReactivate}
          onManage={onManage}
          onUpdateCard={onUpdateCard}
        />
      ))}
    </div>
  )
}

export default SubscriptionsList
